// ScorelinesPage.js — /scorelines: every final score the Premier League has
// produced, most common first, each linking to its own page, plus the
// scorelines inside that range that have never happened.

import React, { useMemo } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, Skeleton } from "@mui/material";
import SEO from "./components/SEO";
import AdSlot from "./components/AdSlot";
import { CtaRow, H2, P, PageHeader, ProsePage } from "./components/Prose";
import { BRAND } from "./theme";
import { formatDate } from "./components/heatmap/utils";
import { fmtScore, numberWord, parseKey, scorelinePath, summaryStats, useSeededQuery } from "./components/scorelines/data";

const SITE_URL = "https://premierleaguescorigami.com";

export default function ScorelinesPage() {
  const summary = useSeededQuery("summary=1");
  const stats = useMemo(() => (summary.data ? summaryStats(summary.data.items || []) : null), [summary.data]);

  const never = useMemo(() => {
    if (!stats) return [];
    const known = new Set(stats.rows.map((r) => r.score));
    const maxGoals = Math.max(...stats.rows.map((r) => parseKey(r.score)[0]));
    const out = [];
    for (let a = 1; a <= maxGoals; a++) for (let b = 0; b <= a; b++) if (!known.has(`${a}-${b}`)) out.push(`${a}-${b}`);
    return out;
  }, [stats]);

  const lede = stats
    ? `${stats.total.toLocaleString()} Premier League matches since August 1992 have finished with ${stats.rows.length} different scores. Here is how often each has happened, from the everyday ${fmtScore(stats.rows[0].score)} to the ${numberWord(stats.oneOffs)} that have happened only once.`
    : "Every final score the Premier League has produced since August 1992, and how often each has happened.";

  return (
    <ProsePage>
      <SEO
        title="Every Premier League scoreline, most common to rarest"
        description="Every final score in Premier League history since 1992, ranked from the most common to the rarest, with home and away counts and when each first happened."
        path="/scorelines"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
            { "@type": "ListItem", position: 2, name: "Scorelines", item: `${SITE_URL}/scorelines` },
          ],
        }}
      />
      <PageHeader eyebrow="Scorelines" title="Every Premier League scoreline" lede={lede} />

      <P>
        Scores are written winner first, so 3–1 covers both a 3–1 home win and a 1–3 away win; each
        scoreline's page splits them. Select one for every match, the clubs involved and its season-by-season
        record.
      </P>

      {summary.error ? (
        <P>The scorelines couldn't be loaded right now. Please try again shortly.</P>
      ) : !stats ? (
        Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} height={40} sx={{ bgcolor: "rgba(255,255,255,0.06)" }} />)
      ) : (
        <ScorelineTable rows={stats.rows} total={stats.total} />
      )}

      <AdSlot position="records" />

      {never.length > 0 && (
        <>
          <H2 id="never">Scores that have never happened</H2>
          <P>
            Within the range the league has reached (up to {Math.max(...never.map((k) => parseKey(k)[0]))} goals for
            one side), these final scores have never happened. The first of them will be the next scorigami.
          </P>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
            {never.map((k) => (
              <Box
                key={k}
                sx={{
                  px: 1.5,
                  py: 0.6,
                  borderRadius: 999,
                  fontSize: 14,
                  fontWeight: 700,
                  color: BRAND.textSecondary,
                  border: "1px dashed rgba(255,255,255,0.18)",
                  fontFeatureSettings: '"tnum"',
                }}
              >
                {fmtScore(k)}
              </Box>
            ))}
          </Box>
        </>
      )}

      <CtaRow
        links={[
          { to: "/", label: "Explore the grid", primary: true },
          { to: "/records", label: "Records" },
          { to: "/articles/rarest-premier-league-scorelines", label: "The rarest scorelines" },
        ]}
      />
    </ProsePage>
  );
}

function ScorelineTable({ rows, total }) {
  return (
    <Box sx={{ overflowX: "auto", mb: 2, mx: { xs: -0.5, sm: 0 } }}>
      <Box
        component="table"
        sx={{
          width: "100%",
          minWidth: 620,
          borderCollapse: "collapse",
          fontFeatureSettings: '"tnum"',
          "& th": {
            textAlign: "right",
            color: BRAND.textSecondary,
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            py: 1,
            px: 1,
            borderBottom: "1px solid rgba(255,255,255,0.12)",
            whiteSpace: "nowrap",
          },
          "& td": { textAlign: "right", py: 1, px: 1, borderBottom: "1px solid rgba(255,255,255,0.06)", color: "#d7dae3", fontSize: 14.5, whiteSpace: "nowrap" },
          "& th:first-of-type, & td:first-of-type": { textAlign: "left", pl: 0 },
          "& td.score a": { color: "#fff", fontWeight: 800, fontSize: 16, textDecoration: "none" },
          "& td.score a:hover": { color: BRAND.cyan },
          "& td.muted": { color: BRAND.textSecondary, fontSize: 13 },
        }}
      >
        <thead>
          <tr>
            <th>Score</th>
            <th>Times</th>
            <th>Share</th>
            <th>Home wins</th>
            <th>Away wins</th>
            <th>First</th>
            <th>Most recent</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const [a, b] = parseKey(r.score);
            const draw = a === b;
            return (
              <tr key={r.score}>
                <td className="score">
                  <RouterLink to={scorelinePath(r.score)}>{fmtScore(r.score)}</RouterLink>
                </td>
                <td>{r.count.toLocaleString()}</td>
                <td>{((r.count / total) * 100).toFixed(r.count / total >= 0.01 ? 1 : 2)}%</td>
                <td>{draw ? "–" : Number(r.home?.count || 0).toLocaleString()}</td>
                <td>{draw ? "–" : Number(r.away?.count || 0).toLocaleString()}</td>
                <td className="muted">{formatDate(r.firstOccurrence.utcDate)}</td>
                <td className="muted">{r.count > 1 ? formatDate(r.latestOccurrence.utcDate) : "–"}</td>
              </tr>
            );
          })}
        </tbody>
      </Box>
    </Box>
  );
}
